import React from 'react';
import { wave } from '../lib/waves';

/*
 * The process, and the clearest argument on the page: speed. A $7M sound wall
 * that takes two years and still fails is the competition.
 *
 * The trace above the steps is one continuous wave that starts as chop over
 * step 00 and arrives flat at step 03 — the same noise-to-silence figure as the
 * logo mark, stretched along the timeline. The graphic is the claim.
 */

/*
 * The commercial ladder, mirrored from the short deck (2026-09-11 pricing
 * model, tools/gslides/build_short_deck.py in zenbul-activity): one
 * deployment in three payment stages. The site study is paid for by
 * the down payment and handed over whatever the customer decides next, which
 * is the thing that makes it a study rather than a sales call.
 *
 * Earlier versions of this section offered a free baseline night and a
 * measured trial on one machine before any guarantee. Both are gone from the
 * deck and gone here, and the word "pilot" stays off the page entirely
 * (Duncan, 2026-09-14). The percentages are the payment shape and stay; the
 * dollar figures do not (see CLAUDE.md, "No prices").
 */
const STEPS = [
  {
    n: '01',
    when: '10% down',
    title: 'Site study',
    body: 'We start planning and engineering your site: the plant’s tonal signature and fan duty cycle, the silencer design, and the wall if one is needed. The sound study is yours to keep, whatever you decide next.',
  },
  {
    n: '02',
    when: '25% at installation',
    title: 'Installation',
    body: 'Tracking silencers on every fan discharge, lined intakes where needed, the sensor array at the boundary, with engineering and submittals.',
  },
  {
    n: '03',
    when: '65% after two weeks’ uptime',
    title: 'Handover',
    body: 'Two weeks of demonstrated uptime for the silencers and the monitoring system, then the number at the boundary is certified and the annual fee starts.',
  },
];

/* Three stacked traces, each damping to near-flat by the right edge. Offset
 * phases keep it from reading as a single tidy sine. */
const TRACES = [
  { d: wave(1200, 34, 15, 11, 0, 0.04), stroke: 'var(--accent)', w: 2.2, o: 0.95 },
  { d: wave(1200, 34, 10, 7, 1.9, 0.04), stroke: 'var(--vermilion-light)', w: 2, o: 0.7 },
  { d: wave(1200, 34, 6.5, 15, 3.4, 0.04), stroke: 'var(--vermilion-light)', w: 1.6, o: 0.45 },
];

function Process() {
  return (
    <section className="section process" id="process">
      <div className="wrap">
        <p className="eyebrow">Process</p>
        <h2 className="h2 process-title">How we start.</h2>
        <p className="lead process-lead">
          One deployment, three payments. The study is yours either way.
        </p>

        <svg
          className="process-trace"
          viewBox="0 0 1200 68"
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          {TRACES.map((t, i) => (
            <path
              key={i}
              d={t.d}
              fill="none"
              stroke={t.stroke}
              strokeWidth={t.w}
              strokeLinecap="round"
              opacity={t.o}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>

        <ol className="process-grid">
          {STEPS.map((s) => (
            <li className="process-step" key={s.n}>
              <span className="process-step-n">{s.n}</span>
              <span className="process-step-when">{s.when}</span>
              <h3 className="h3 process-step-title">{s.title}</h3>
              <p className="process-step-body">{s.body}</p>
            </li>
          ))}
        </ol>

        {/* The "so what" for the timeline above. Speed is the product here:
            a noise objection is schedule risk, and schedule risk is the thing
            that actually moves the return on a build. Kept to one line so the
            section stays a process, not a pitch. */}
        <p className="process-outcome">
          <strong className="process-outcome-lead">
            Faster through permitting. Faster to energized. Faster to a return
            on the capital already committed.
          </strong>{' '}
          A noise objection can hold a datacenter for years. Clearing it in
          months protects the schedule you underwrote.
        </p>
      </div>
    </section>
  );
}

export default Process;
